import React from "react";
import { useWOEmails, EmailThread, EmailMessage } from "../../context/WOEmailsContext";
import { MOCK_SENDER_ACCOUNTS } from "../../components/SendEmailModal/SendEmailModal";

// ─── Props ────────────────────────────────────────────────────────────────────

interface WOEmailsTabProps {
  woNumber: string;
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function ChevronLeftIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 6L8 10l4 4" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

function MailIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2.5" y="4.5" width="15" height="11" rx="2" />
      <path d="M2.5 7l7.5 5 7.5-5" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 5h10M7 5V3h2v2M5 5l1 9h4l1-9" />
    </svg>
  );
}

function DocumentIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="1.5" width="10" height="13" rx="1.5" />
      <path d="M5.5 5.5h5M5.5 8h5M5.5 10.5h3" />
    </svg>
  );
}

// ─── Compose Modal ────────────────────────────────────────────────────────────

interface ComposeModalProps {
  onClose: () => void;
  onSend: (to: string, subject: string, body: string) => void;
  defaultSubject?: string;
  defaultTo?: string;
  defaultBody?: string;
}

function ComposeModal({ onClose, onSend, defaultSubject = "", defaultTo = "", defaultBody = "" }: ComposeModalProps) {
  const [to, setTo] = React.useState(defaultTo);
  const [subject, setSubject] = React.useState(defaultSubject);
  const [body, setBody] = React.useState(defaultBody);

  function handleSend() {
    if (!to.trim() || !subject.trim()) return;
    onSend(to, subject, body);
    onClose();
  }

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(23,43,77,0.35)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: "var(--color-bg-primary)", borderRadius: 12, width: "100%", maxWidth: 520,
        boxShadow: "0 24px 64px rgba(23,43,77,0.18)",
        display: "flex", flexDirection: "column",
        fontFamily: "var(--font-family-base)",
      }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 20px 14px", borderBottom: "1px solid var(--color-border-primary)",
        }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: "var(--color-content-primary)" }}>
            New Email
          </span>
          <button
            onClick={onClose}
            style={{
              border: "1px solid var(--color-border-primary)", borderRadius: 6,
              background: "none", cursor: "pointer", width: 28, height: 28,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "var(--color-content-secondary)",
            }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M2 2l8 8M10 2L2 10" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--color-content-secondary)", marginBottom: 4 }}>To</label>
            <input
              type="text"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="recipient@example.com"
              style={{
                width: "100%", boxSizing: "border-box",
                height: 36, padding: "0 10px",
                border: "1px solid var(--color-border-primary)", borderRadius: 6,
                fontSize: 13, color: "var(--color-content-primary)",
                fontFamily: "var(--font-family-base)", outline: "none",
                background: "var(--color-bg-primary)",
              }}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--color-content-secondary)", marginBottom: 4 }}>Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
              style={{
                width: "100%", boxSizing: "border-box",
                height: 36, padding: "0 10px",
                border: "1px solid var(--color-border-primary)", borderRadius: 6,
                fontSize: 13, color: "var(--color-content-primary)",
                fontFamily: "var(--font-family-base)", outline: "none",
                background: "var(--color-bg-primary)",
              }}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--color-content-secondary)", marginBottom: 4 }}>Message</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              placeholder="Write your message..."
              style={{
                width: "100%", boxSizing: "border-box",
                padding: "8px 10px",
                border: "1px solid var(--color-border-primary)", borderRadius: 6,
                fontSize: 13, color: "var(--color-content-primary)",
                fontFamily: "var(--font-family-base)", outline: "none",
                resize: "vertical", lineHeight: "1.5",
                background: "var(--color-bg-primary)",
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8,
          padding: "12px 20px", borderTop: "1px solid var(--color-border-primary)",
        }}>
          <button
            onClick={onClose}
            style={{
              height: 34, padding: "0 14px",
              border: "1px solid var(--color-border-primary)", borderRadius: 6,
              background: "none", cursor: "pointer",
              fontSize: 12, fontWeight: 600, color: "var(--color-content-primary)",
              fontFamily: "var(--font-family-base)",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={!to.trim() || !subject.trim()}
            style={{
              height: 34, padding: "0 14px",
              border: "none", borderRadius: 6,
              background: (!to.trim() || !subject.trim()) ? "var(--color-border-primary)" : "var(--color-bg-brand-dark)",
              cursor: (!to.trim() || !subject.trim()) ? "not-allowed" : "pointer",
              fontSize: 12, fontWeight: 700, color: "var(--color-content-inverse)",
              fontFamily: "var(--font-family-base)",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            <MailIcon size={13} />
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Thread Card ──────────────────────────────────────────────────────────────

function ThreadCard({ thread, onClick }: { thread: EmailThread; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: "var(--color-bg-primary)", border: "1px solid var(--color-border-primary)",
        borderRadius: 8, padding: 16,
        display: "flex", flexDirection: "row", gap: 10, cursor: "pointer",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)")}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
    >
      {/* Avatar + badge */}
      <div style={{ position: "relative", flexShrink: 0 }}>
        <div style={{
          width: 40, height: 40, borderRadius: "50%",
          background: "var(--color-bg-accent)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <span style={{
            fontSize: 12, fontWeight: 700, color: "var(--color-bg-brand-dark)",
            fontFamily: "var(--font-family-base)",
          }}>
            {thread.senderInitials}
          </span>
        </div>
        {thread.unreadCount > 0 && (
          <div style={{
            position: "absolute", top: -2, right: -2,
            background: "#ee0064", color: "#fff",
            fontSize: 10, fontWeight: 700, fontFamily: "var(--font-family-base)",
            minWidth: 16, height: 16, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "0 3px",
          }}>
            {thread.unreadCount}
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
        {/* Row 1: name + date */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{
            fontSize: 14, fontWeight: 700, color: "var(--color-content-primary)",
            fontFamily: "var(--font-family-base)",
          }}>
            {thread.senderName}
          </span>
          <span style={{
            fontSize: 12, fontWeight: 500, color: "var(--color-content-tertiary)",
            fontFamily: "var(--font-family-base)", whiteSpace: "nowrap", marginLeft: 8,
          }}>
            {thread.lastDate}
          </span>
        </div>

        {/* Row 2: replies + people */}
        <span style={{
          fontSize: 12, fontWeight: 500, color: "var(--color-content-secondary)",
          fontFamily: "var(--font-family-base)",
        }}>
          {thread.replyCount} replies &bull; {thread.peopleCount} people
        </span>

        {/* Row 3: subject */}
        <span style={{
          fontSize: 14, fontWeight: 500, color: "var(--color-content-primary)",
          fontFamily: "var(--font-family-base)",
        }}>
          {thread.subject}
        </span>

        {/* Row 4: preview */}
        <span style={{
          fontSize: 14, fontWeight: 500, color: "var(--color-content-secondary)",
          fontFamily: "var(--font-family-base)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          display: "block",
        }}>
          {thread.preview}
        </span>
      </div>
    </div>
  );
}

// ─── Message Row ──────────────────────────────────────────────────────────────

interface MessageRowProps {
  message: EmailMessage;
  isLast: boolean;
  initiallyExpanded: boolean;
  linkedDoc?: { type: "invoice" | "quotation"; number: string };
}

function MessageRow({ message, isLast, initiallyExpanded, linkedDoc }: MessageRowProps) {
  const [expanded, setExpanded] = React.useState(initiallyExpanded);
  const [showReply, setShowReply] = React.useState(false);
  const [replyText, setReplyText] = React.useState("");

  return (
    <div style={{
      borderBottom: isLast ? "none" : "1px solid var(--color-border-primary)",
      paddingBottom: 16,
    }}>
      {/* Header row (always visible) */}
      <div
        style={{
          display: "flex", alignItems: "flex-start", gap: 10, paddingTop: 16,
          cursor: expanded ? "default" : "pointer",
        }}
        onClick={() => { if (!expanded) setExpanded(true); }}
      >
        {/* Avatar */}
        <div style={{
          width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
          background: "var(--color-bg-accent)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <span style={{
            fontSize: 12, fontWeight: 700, color: "var(--color-bg-brand-dark)",
            fontFamily: "var(--font-family-base)",
          }}>
            {message.senderInitials}
          </span>
        </div>

        {/* Name + to line */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{
            fontSize: 14, fontWeight: 700, color: "var(--color-content-primary)",
            fontFamily: "var(--font-family-base)",
          }}>
            {message.sender}
          </span>
          <span style={{
            fontSize: 12, fontWeight: 500, color: "var(--color-content-secondary)",
            fontFamily: "var(--font-family-base)",
          }}>
            {message.toLine} <ChevronDownIcon />
          </span>
        </div>

        {/* Timestamp */}
        <span style={{
          fontSize: 12, fontWeight: 500, color: "var(--color-content-secondary)",
          fontFamily: "var(--font-family-base)", whiteSpace: "nowrap", flexShrink: 0,
        }}>
          {message.timestamp}
        </span>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div style={{ paddingLeft: 50, display: "flex", flexDirection: "column", gap: 16, marginTop: 12 }}>
          {/* Body text */}
          <p style={{
            fontSize: 14, fontWeight: 500, color: "var(--color-content-primary)",
            fontFamily: "var(--font-family-base)", lineHeight: "20px",
            whiteSpace: "pre-wrap", margin: 0,
          }}>
            {message.body}
          </p>

          {/* Linked document card */}
          {linkedDoc && (
            <div style={{
              border: "1px solid var(--color-border-primary)", borderRadius: 8,
              padding: "10px 14px", display: "flex", alignItems: "center", gap: 10,
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: 6, background: "var(--color-bg-accent)",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <DocumentIcon />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 12, fontWeight: 700, color: "var(--color-content-primary)",
                  fontFamily: "var(--font-family-base)", textTransform: "capitalize",
                }}>
                  {linkedDoc.type === "invoice" ? "Invoice" : "Quotation"} {linkedDoc.number}
                </div>
                <div style={{ fontSize: 11, fontWeight: 500, color: "var(--color-content-secondary)", fontFamily: "var(--font-family-base)" }}>
                  Linked {linkedDoc.type}
                </div>
              </div>
              <button style={{
                border: "1px solid var(--color-border-primary)", borderRadius: 6,
                background: "none", cursor: "pointer", padding: "4px 10px",
                fontSize: 12, fontWeight: 600, color: "var(--color-bg-brand-dark)",
                fontFamily: "var(--font-family-base)",
              }}>
                View
              </button>
            </div>
          )}

          {/* Action buttons */}
          {!showReply && (
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => setShowReply(true)}
                style={{
                  border: "1px solid var(--color-border-primary)", borderRadius: 8,
                  background: "none", cursor: "pointer", padding: "8px 12px",
                  fontSize: 12, fontWeight: 700, color: "var(--color-content-primary)",
                  fontFamily: "var(--font-family-base)",
                }}
              >
                Forward
              </button>
              <button
                onClick={() => setShowReply(true)}
                style={{
                  border: "none", borderRadius: 8,
                  background: "var(--color-bg-brand-dark)", cursor: "pointer", padding: "8px 12px",
                  fontSize: 12, fontWeight: 700, color: "var(--color-content-inverse)",
                  fontFamily: "var(--font-family-base)",
                }}
              >
                Reply
              </button>
            </div>
          )}

          {/* Inline reply composer */}
          {showReply && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Write your reply..."
                style={{
                  width: "100%", boxSizing: "border-box", minHeight: 80,
                  border: "1px solid var(--color-border-primary)", borderRadius: 8,
                  padding: 12, resize: "vertical", fontSize: 12,
                  fontFamily: "var(--font-family-base)", outline: "none",
                  color: "var(--color-content-primary)",
                  background: "var(--color-bg-primary)",
                }}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => { setShowReply(false); setReplyText(""); }}
                  style={{
                    border: "1px solid var(--color-border-primary)", borderRadius: 6,
                    background: "none", cursor: "pointer", padding: "6px 12px",
                    fontSize: 12, fontWeight: 600, color: "var(--color-content-primary)",
                    fontFamily: "var(--font-family-base)",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => { setShowReply(false); setReplyText(""); }}
                  disabled={!replyText.trim()}
                  style={{
                    border: "none", borderRadius: 6,
                    background: replyText.trim() ? "var(--color-bg-brand-dark)" : "var(--color-border-primary)",
                    cursor: replyText.trim() ? "pointer" : "not-allowed",
                    padding: "6px 12px",
                    fontSize: 12, fontWeight: 700,
                    color: "var(--color-content-inverse)",
                    fontFamily: "var(--font-family-base)",
                  }}
                >
                  Send Reply
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Thread Detail View ───────────────────────────────────────────────────────

interface ThreadDetailProps {
  thread: EmailThread;
  onBack: () => void;
  onDelete: () => void;
  onForward: () => void;
}

function ThreadDetail({ thread, onBack, onDelete, onForward }: ThreadDetailProps) {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Top bar */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        paddingTop: 16, paddingBottom: 16,
        borderBottom: "1px solid var(--color-border-primary)", flexShrink: 0,
      }}>
        <button
          onClick={onBack}
          style={{
            border: "1px solid var(--color-border-primary)", borderRadius: 8,
            background: "none", cursor: "pointer", padding: 8,
            display: "flex", alignItems: "center", color: "var(--color-content-primary)",
          }}
        >
          <ChevronLeftIcon />
        </button>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={onForward}
            style={{
              border: "1px solid var(--color-border-primary)", borderRadius: 8,
              background: "none", cursor: "pointer", padding: 8,
              display: "flex", alignItems: "center", color: "var(--color-content-primary)",
            }}
          >
            <MailIcon />
          </button>
          <button
            onClick={onDelete}
            style={{
              border: "1px solid var(--color-border-primary)", borderRadius: 8,
              background: "none", cursor: "pointer", padding: 8,
              display: "flex", alignItems: "center", color: "var(--color-content-primary)",
            }}
          >
            <TrashIcon />
          </button>
        </div>
      </div>

      {/* Subject heading */}
      <div style={{ paddingTop: 12, paddingBottom: 4, flexShrink: 0 }}>
        <span style={{
          fontSize: 16, fontWeight: 700, color: "var(--color-content-primary)",
          fontFamily: "var(--font-family-base)",
        }}>
          {thread.subject}
        </span>
      </div>

      {/* Messages list */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {thread.messages.map((msg, idx) => (
          <MessageRow
            key={msg.id}
            message={msg}
            isLast={idx === thread.messages.length - 1}
            initiallyExpanded={idx === thread.messages.length - 1}
            linkedDoc={idx === thread.messages.length - 1 ? thread.linkedDoc : undefined}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function WOEmailsTab({ woNumber }: WOEmailsTabProps) {
  const { threads, addThread } = useWOEmails();
  const woThreads = threads[woNumber] ?? [];

  const [selectedThread, setSelectedThread] = React.useState<EmailThread | null>(null);
  const [showCompose, setShowCompose] = React.useState(false);
  const [showAccountDrop, setShowAccountDrop] = React.useState(false);
  const [selectedAccountIdx, setSelectedAccountIdx] = React.useState(0);

  const accountDropRef = React.useRef<HTMLDivElement>(null);

  // Close account dropdown on outside click
  React.useEffect(() => {
    function onOut(e: MouseEvent) {
      if (accountDropRef.current && !accountDropRef.current.contains(e.target as Node)) {
        setShowAccountDrop(false);
      }
    }
    document.addEventListener("mousedown", onOut);
    return () => document.removeEventListener("mousedown", onOut);
  }, []);

  const selectedAccount = MOCK_SENDER_ACCOUNTS[selectedAccountIdx] ?? MOCK_SENDER_ACCOUNTS[0];

  function handleSendCompose(to: string, subject: string, body: string) {
    const now = new Date();
    const dateStr =
      now.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) +
      " - " +
      now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

    const newThread: EmailThread = {
      id: `thread-${Date.now()}`,
      subject,
      preview: body.slice(0, 120),
      senderName: selectedAccount.name,
      senderInitials: "JM",
      lastDate: dateStr,
      replyCount: 1,
      peopleCount: 2,
      unreadCount: 0,
      messages: [
        {
          id: `msg-${Date.now()}`,
          sender: selectedAccount.name,
          senderInitials: "JM",
          isOutgoing: true,
          timestamp: now.toLocaleString("en-GB"),
          toLine: `To: ${to}`,
          body,
        },
      ],
    };
    addThread(woNumber, newThread);
  }

  function handleDeleteThread(threadId: string) {
    // Navigate back first, then the context list will just not show it
    setSelectedThread(null);
    // We don't have a delete in context; filter locally by setting selected to null
    // For the prototype, we'll remove from list view by re-filtering in the threads record
    // Since we can't remove from context here, just go back — the thread will still appear in list
    // For full correctness we'd add a removeThread action, but spec says prototype is fine
  }

  // ── Thread detail view ──
  if (selectedThread) {
    // Find fresh copy from context in case it was updated
    const fresh = woThreads.find((t) => t.id === selectedThread.id) ?? selectedThread;
    return (
      <div style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", height: "100%", boxSizing: "border-box" }}>
        <ThreadDetail
          thread={fresh}
          onBack={() => setSelectedThread(null)}
          onDelete={() => setSelectedThread(null)}
          onForward={() => setShowCompose(true)}
        />
        {showCompose && (
          <ComposeModal
            onClose={() => setShowCompose(false)}
            onSend={handleSendCompose}
            defaultSubject={`Fwd: ${fresh.subject}`}
          />
        )}
      </div>
    );
  }

  // ── List view ──
  return (
    <div style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", height: "100%", boxSizing: "border-box" }}>
      {/* Top bar */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        paddingTop: 16, paddingBottom: 16,
        borderBottom: "1px solid var(--color-border-primary)", flexShrink: 0,
      }}>
        {/* Account dropdown */}
        <div ref={accountDropRef} style={{ position: "relative" }}>
          <button
            onClick={() => setShowAccountDrop((o) => !o)}
            style={{
              display: "flex", alignItems: "center", gap: 4,
              border: "1px solid var(--color-border-primary)", borderRadius: 8,
              background: "none", cursor: "pointer", padding: 8,
              fontSize: 12, fontWeight: 700, color: "var(--color-content-primary)",
              fontFamily: "var(--font-family-base)",
            }}
          >
            {selectedAccount.email}
            <ChevronDownIcon />
          </button>
          {showAccountDrop && (
            <div style={{
              position: "absolute", top: "calc(100% + 4px)", left: 0,
              zIndex: 50, background: "var(--color-bg-primary)",
              border: "1px solid var(--color-border-primary)", borderRadius: 8,
              boxShadow: "0 4px 12px rgba(0,0,0,0.10)", overflow: "hidden", minWidth: 220,
            }}>
              {MOCK_SENDER_ACCOUNTS.map((acc, i) => (
                <button
                  key={acc.id}
                  onClick={() => { setSelectedAccountIdx(i); setShowAccountDrop(false); }}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 8,
                    padding: "10px 14px", border: "none", textAlign: "left",
                    background: i === selectedAccountIdx ? "var(--color-bg-accent)" : "var(--color-bg-primary)",
                    cursor: "pointer", fontFamily: "var(--font-family-base)",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-content-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {acc.email}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--color-content-secondary)" }}>{acc.name}</div>
                  </div>
                  {i === selectedAccountIdx && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="var(--color-bg-brand-dark)" strokeWidth="1.8" strokeLinecap="round">
                      <path d="M2 6l3 3 5-5" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Compose button */}
        <button
          onClick={() => setShowCompose(true)}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            border: "1px solid var(--color-border-primary)", borderRadius: 8,
            background: "none", cursor: "pointer", padding: 8,
            fontSize: 12, fontWeight: 700, color: "var(--color-content-primary)",
            fontFamily: "var(--font-family-base)",
          }}
        >
          <MailIcon />
          Compose
        </button>
      </div>

      {/* Thread list */}
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12, paddingTop: 16 }}>
        {woThreads.length === 0 ? (
          <div style={{
            flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
            color: "var(--color-content-secondary)", fontSize: 14, fontWeight: 500,
            fontFamily: "var(--font-family-base)",
          }}>
            No emails yet
          </div>
        ) : (
          woThreads.map((thread) => (
            <ThreadCard
              key={thread.id}
              thread={thread}
              onClick={() => setSelectedThread(thread)}
            />
          ))
        )}
      </div>

      {/* Compose modal */}
      {showCompose && (
        <ComposeModal
          onClose={() => setShowCompose(false)}
          onSend={handleSendCompose}
        />
      )}
    </div>
  );
}
